import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isSupabaseAvailable } from '../../utils/environment';
import { useAuth } from '../../hooks/useAuth';
import logger from '../../utils/logger';
import ProductionLoginForm from '../../components/auth/ProductionLoginForm';
import LoginDebugInfo from '../../components/debug/LoginDebugInfo';
import { DataServiceError, ErrorCodes } from '../../services/dataService';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithEmail, resetPassword } = useAuth();
  const [loginError, setLoginError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // 環境変数デバッグログ
  React.useEffect(() => {
    logger.info('🔍 LoginPage環境変数確認:', {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
      isSupabaseAvailable: isSupabaseAvailable(),
    });
  }, []);

  // ログインハンドラー（全モード対応）
  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    setLoginError('');

    try {
      await loginWithEmail(email, password);
      navigate('/');
    } catch (error) {
      logger.error('Login error:', error);

      // エラーの種類に応じたメッセージ表示
      if (error instanceof DataServiceError) {
        if (error.code === ErrorCodes.COMPANY_INACTIVE) {
          setLoginError(error.message);
        } else if (error.code === ErrorCodes.AUTHENTICATION_FAILED) {
          setLoginError('メールアドレスまたはパスワードが正しくありません。');
        } else {
          setLoginError(error.message || 'ログインに失敗しました。');
        }
      } else {
        setLoginError('メールアドレスまたはパスワードが正しくありません。');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // パスワードリセットハンドラー
  const handleResetPassword = async (email: string) => {
    if (!resetPassword) {
      throw new Error('パスワードリセット機能は現在のモードでは利用できません');
    }

    try {
      await resetPassword(email);
    } catch (error) {
      logger.error('Password reset error:', error);
      throw error; // ProductionLoginFormでエラーハンドリング
    }
  };

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center items-center p-4'>
      <div className='max-w-md w-full mx-auto'>
        <div className='flex justify-center items-center mb-6'>
          <img src='/logo.webp' alt='ラクハイ' className='h-12 w-12 sm:h-16 sm:w-16' />
          <h1 className='ml-3 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white'>
            ラクハイ
          </h1>
        </div>
        <div className='text-center mb-8'>
          <p className='text-gray-600 dark:text-gray-300'>
            現代の物流のためのインテリジェントなプラットフォーム。
          </p>
        </div>

        {/* メイン認証フォーム */}
        <ProductionLoginForm
          onLogin={handleLogin}
          onResetPassword={handleResetPassword}
          loading={isLoading}
          error={loginError}
        />

        {/* 開発環境用デバッグ情報 */}
        <LoginDebugInfo />

        <p className='text-center text-sm text-gray-500 dark:text-gray-400 mt-8'>
          © 2025 ラクハイ All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
